import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import tokenService from '../services/tokenService';
import { setUnauthorizedHandler } from '../api/client';
import { normalizeRole, homePathForRole } from '../constants/roles';
import { showToast } from '../components/Toast';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  const logout = useCallback((reason) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    setSessionWarning(null);
    tokenService.stopTokenValidation();
    if (reason) {
      showToast(reason, 'warning');
    }
    navigate('/login', { replace: true });
  }, [navigate]);

  const login = useCallback((token, userData) => {
    const normalized = { ...userData, role: normalizeRole(userData.role) };
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(normalized));
    setIsAuthenticated(true);
    setUser(normalized);
    tokenService.setupAxiosInterceptor((message) => logout(message));
    tokenService.startTokenValidation(
      (message) => logout(message),
      (timeUntilExpiry, expiresAt) => {
        const formatted = tokenService.formatTimeUntilExpiry(timeUntilExpiry);
        setSessionWarning({ message: `Session expires in ${formatted}`, expiresAt });
        setTimeout(() => setSessionWarning(null), 10000);
      }
    );
  }, [logout]);

  const updateUser = useCallback((partial) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    setUnauthorizedHandler((message) => logout(message));

    async function bootstrap() {
      const hash = (window.location.hash || '').replace(/^#/, '');
      const params = new URLSearchParams(hash);
      const access = params.get('cn_access');
      if (access) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        try {
          const res = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${access}`, Accept: 'application/json' },
          });
          const data = await res.json();
          if (!res.ok || !data.user) {
            throw new Error(data.message || data.detail || 'Google/Apple sign-in failed');
          }
          login(access, data.user);
        } catch (err) {
          showToast(err.message || 'Google/Apple sign-in failed', 'error');
        }
        setBootstrapping(false);
        return;
      }

      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (token && userData) {
        try {
          const parsed = JSON.parse(userData);
          parsed.role = normalizeRole(parsed.role);
          setIsAuthenticated(true);
          setUser(parsed);
          tokenService.setupAxiosInterceptor((message) => logout(message));
          tokenService.startTokenValidation(
            (message) => logout(message),
            (timeUntilExpiry) => {
              const formatted = tokenService.formatTimeUntilExpiry(timeUntilExpiry);
              setSessionWarning({ message: `Session expires in ${formatted}` });
              setTimeout(() => setSessionWarning(null), 10000);
            }
          );
        } catch {
          logout();
        }
      }
      setBootstrapping(false);
    }

    bootstrap();
    return () => tokenService.stopTokenValidation();
  }, [logout, login]);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    bootstrapping,
    sessionWarning,
    login,
    updateUser,
    logout: () => logout(),
    dismissSessionWarning: () => setSessionWarning(null),
    homePath: user ? homePathForRole(user.role) : '/login',
  }), [user, isAuthenticated, bootstrapping, sessionWarning, login, updateUser, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
